import type { Node } from '../../../api/table-of-content';
import type { Module } from '../../../api/module';
import type { NodeWithoutDescription } from '../../../api/table-of-content';
import type { Medialink } from '../../../api/medialink';
import type { ValuesOfObject } from '../../../@types/utils';
import type { ANALYTICS_EVENT_SUBLOCATIONS } from '../../../constants/analyticsEvents';
import { useEffect, useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import List from '@pelckmans/elementis/components/list';
import DropdownMenu from '@pelckmans/elementis/components/dropdown-menu';
import DisplayOn from '@pelckmans/elementis/icons/display-on';
import Share from '@pelckmans/elementis/icons/share';
import IdentityContext from '../../../contexts/IdentityContext';
import SelectionContext from '../toc/_contexts/SelectionContext';
import ModalContext from '../../../contexts/ModalContext';
import PinnedMaterialButton from '../toc/components/pinned-material/PinnedMaterialButton';
import ActionMenuButton from '../../../shared-components/ActionMenuButton';
import ShareModal from '../../../shared-components/share-modal/ShareModal';
import { MaterialIcon } from './MaterialIcon';
import useMedialinks from '../toc/hooks/medialink/useMedialinks';
import { isScorableMedialink, isUnlicensedMedialink, validateAndGetLinkFromMedialink } from '../../../api/medialink';
import ScorableProgress from '../toc/components/ScorableProgress';

type Props = {
  node: Node | NodeWithoutDescription | undefined;
  module: Module;
  medialink: Medialink;
  canBePinned?: boolean;
  subLocationId?: ValuesOfObject<typeof ANALYTICS_EVENT_SUBLOCATIONS>;
  refetch: () => void;
  getPillGroup: (
    medialink: Medialink,
    isTeacherAlike: boolean,
  ) =>
    | {
        'aria-label': undefined;
        children: NonNullable<React.ReactNode>;
        role: undefined;
      }
    | undefined;
  showActionMenu?: boolean;
  openMedialinkId?: string | null;
  onMedialinkIsOpened?: () => void;
};

export default function MediaLinkItem({
  node: selectedNode,
  module: selectedModule,
  medialink,
  canBePinned,
  showActionMenu = true,
  subLocationId,
  refetch,
  getPillGroup,
  openMedialinkId,
  onMedialinkIsOpened,
}: Props) {
  const { t } = useTranslation();
  const { openMedialink } = useMedialinks();

  const {
    identity: { isTeacherAlike },
  } = useContext(IdentityContext);
  const { showModal } = useContext(ModalContext);
  const { showSelection, selectedMedialinks, addMedialinks, removeMedialinks } = useContext(SelectionContext);
  const link = validateAndGetLinkFromMedialink(medialink);

  const handleOpenMedialink = useCallback(
    async (medialink: Medialink) => {
      if (!selectedNode || !selectedModule) return;

      const modal = await openMedialink(
        medialink,
        selectedNode,
        selectedModule,
        isScorableMedialink(medialink) ? refetch : undefined,
        undefined,
        subLocationId,
      );

      if (modal) {
        showModal(modal);
      }
    },
    [selectedNode, selectedModule, openMedialink, refetch, subLocationId, showModal],
  );

  useEffect(() => {
    if (openMedialinkId && medialink.id === openMedialinkId) {
      if (medialink) {
        handleOpenMedialink(medialink);
        onMedialinkIsOpened?.();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openMedialinkId, medialink]); // openMedialin, onMedialinkIsOpened is not stable

  const showShareModal = (medialink: Medialink) => {
    if (!selectedModule || !selectedNode) return;
    return showModal(
      <ShareModal
        id="share-modal"
        moduleId={selectedModule.id}
        nodeId={selectedNode.id}
        medialinks={[medialink]}
        onSubmitted={refetch}
      />,
    );
  };

  const contentArea = isScorableMedialink(medialink) && (
    <ScorableProgress unachievable={medialink.unachievable} score={medialink.score} />
  );

  const disabled = isUnlicensedMedialink(medialink);

  function ActionsMenu() {
    return (
      <ActionMenuButton
        disabled={disabled}
        menuProps={{
          ariaLabel: t('PAGES.TOC_LEVEL.ACTION_MENU.A11Y_LABEL'),
        }}
        buttonProps={{
          ariaLabel: t('PAGES.TOC_LEVEL.ACTION_MENU.BUTTON.A11Y_LABEL'),
        }}
      >
        {handleToggle => (
          <>
            <DropdownMenu.Item
              text={t('PAGES.TOC_LEVEL.ACTION_MENU.OPTIONS.OPEN_MATERIAL.LABEL')}
              textIcon={<DisplayOn />}
              as="button"
              role="menuitem"
              onClick={() => {
                handleOpenMedialink(medialink);
                handleToggle();
              }}
              disabled={disabled}
            />
            <DropdownMenu.Item
              text={t('PAGES.TOC_LEVEL.ACTION_MENU.OPTIONS.SHARE_MATERIAL.LABEL')}
              textIcon={<Share />}
              as="button"
              role="menuitem"
              showSeparator={true}
              onClick={() => {
                showShareModal(medialink);
                handleToggle();
              }}
              disabled={disabled}
            />
          </>
        )}
      </ActionMenuButton>
    );
  }

  const actionArea = (
    <>
      {canBePinned && <PinnedMaterialButton type="medialink" materialId={medialink.id} disabled={disabled} />}
      {!showSelection && showActionMenu && isTeacherAlike && <ActionsMenu />}
    </>
  );

  const pillGroupProps = getPillGroup(medialink, isTeacherAlike);

  if (showSelection) {
    const isSelected = selectedMedialinks.includes(medialink);

    return (
      <List.Item
        key={medialink.id}
        title={medialink.name}
        contentArea={contentArea}
        actionArea={actionArea}
        pillGroupProps={pillGroupProps}
        as="checkbox"
        checked={isSelected}
        onClick={() => (isSelected ? removeMedialinks([medialink]) : addMedialinks([medialink]))}
        disabled={disabled}
      />
    );
  }

  if (link) {
    return (
      <List.Item
        key={medialink.id}
        title={medialink.name}
        icon={<MaterialIcon materialType="medialink" contentType={medialink.contentType} />}
        contentArea={contentArea}
        actionArea={actionArea}
        pillGroupProps={pillGroupProps}
        as="link"
        href={link}
        target="_blank"
        rel="noopener noreferrer"
        disabled={disabled}
      />
    );
  }

  return (
    <List.Item
      key={medialink.id}
      title={medialink.name}
      icon={<MaterialIcon materialType="medialink" contentType={medialink.contentType} />}
      contentArea={contentArea}
      actionArea={actionArea}
      pillGroupProps={pillGroupProps}
      as="button"
      onClick={() => {
        handleOpenMedialink(medialink);
      }}
      disabled={disabled}
    />
  );
}
